import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Order, CustomerType } from '../../../types/order';
import { getDaysSinceOrder, getOrderRowBackgroundColor } from '../../../utils/orderUtils';

interface OrderSummaryProps {
  orders: Order[];
  onNavigateToOrder: (orderId: string) => void;
}

interface OrderDetail {
  customerName: string;
  orderDate: string;
  quantity: number;
  orderId: string;
  status: string;
  clientId?: string;
  customerType: CustomerType;
}

interface FlavorSummaryData {
  count: number;
  oldestOrderDate: string | null;
  oldestOrderStatus: string;
  orderDetails: OrderDetail[];
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({ orders, onNavigateToOrder }) => {
  const [expandedFlavors, setExpandedFlavors] = useState<Set<string>>(new Set());
  const [isSectionCollapsed, setIsSectionCollapsed] = useState(true);

  // Helper function to render customer type badge
  const renderCustomerTypeBadge = (customerType: CustomerType) => {
    const isRetailer = customerType?.toLowerCase() === 'retailer';

    return (
      <span
        style={{
          fontSize: '0.65rem',
          padding: '2px 6px',
          backgroundColor: isRetailer ? '#9C27B0' : '#FF9800',
          color: 'white',
          borderRadius: '3px',
          fontWeight: 'bold',
          marginLeft: '6px'
        }}
      >
        {isRetailer ? 'RETAILER' : 'INDIVIDUAL'}
      </span>
    );
  };

  // Calculate flavor summary using useMemo for performance
  const flavorSummary = useMemo(() => {
    const summary: Record<string, FlavorSummaryData> = {};

    // Aggregate all items from all orders
    orders.forEach(order => {
      order.items.forEach(item => {
        const flavorName = item.recipeName;
        if (flavorName) {
          // Initialize if not exists
          if (!summary[flavorName]) {
            summary[flavorName] = {
              count: 0,
              oldestOrderDate: null,
              oldestOrderStatus: 'Pending',
              orderDetails: []
            };
          }

          // Add to count
          summary[flavorName].count += item.quantity;

          // Track order details for this flavor
          summary[flavorName].orderDetails.push({
            customerName: order.customerName,
            orderDate: order.orderDate,
            quantity: item.quantity,
            orderId: order.id,
            status: order.status,
            clientId: order.clientId,
            customerType: order.customerType
          });

          // Track oldest pending order for this flavor
          if (order.status !== 'Completed') {
            const currentOldestDate = summary[flavorName].oldestOrderDate;
            if (!currentOldestDate || order.orderDate < currentOldestDate) {
              summary[flavorName].oldestOrderDate = order.orderDate;
              summary[flavorName].oldestOrderStatus = order.status;
            }
          }
        }
      });
    });

    // Convert to sorted array for display - sort by quantity descending (highest first)
    return Object.entries(summary)
      .sort(([, dataA], [, dataB]) => dataB.count - dataA.count)
      .map(([name, data]) => ({
        name,
        count: data.count,
        oldestOrderDate: data.oldestOrderDate,
        oldestOrderStatus: data.oldestOrderStatus,
        orderDetails: data.orderDetails
      }));
  }, [orders]);

  // Toggle expand/collapse for a flavor
  const toggleFlavor = (flavorName: string) => {
    setExpandedFlavors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(flavorName)) {
        newSet.delete(flavorName);
      } else {
        newSet.add(flavorName);
      }
      return newSet;
    });
  };

  // Don't render if no flavors
  if (flavorSummary.length === 0) {
    return null;
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div
        style={{
          marginBottom: '24px',
          padding: '20px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}
      >
        <div
          onClick={() => setIsSectionCollapsed(!isSectionCollapsed)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: isSectionCollapsed ? '0' : '16px',
            cursor: 'pointer',
            userSelect: 'none',
            padding: '8px',
            margin: '-8px',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease-in-out'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.03)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <span
            style={{
              transform: isSectionCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
              transition: 'transform 0.2s ease-in-out',
              display: 'inline-block',
              fontSize: '1rem',
              color: '#666'
            }}
          >
            ▶
          </span>
          <h2
            style={{
              fontSize: '1.5rem',
              color: '#333',
              margin: '0',
              fontWeight: '600'
            }}
          >
            Order Summary - Units by Flavor
          </h2>
        </div>

        {!isSectionCollapsed && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '12px',
              animation: 'fadeIn 0.2s ease-in-out'
            }}
          >
            {flavorSummary.map(({ name, count, oldestOrderDate, oldestOrderStatus, orderDetails }) => {
              // Calculate background color based on oldest pending order
              let backgroundColor = 'white';
              if (oldestOrderDate) {
                const daysSince = getDaysSinceOrder(oldestOrderDate);
                backgroundColor = getOrderRowBackgroundColor(daysSince, oldestOrderStatus);
              }

              const isExpanded = expandedFlavors.has(name);

              return (
                <div
                  key={name}
                  style={{
                    backgroundColor,
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  {/* Header - clickable */}
                  <div
                    onClick={() => toggleFlavor(name)}
                    style={{
                      padding: '12px 16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {/* Arrow icon */}
                      <span
                        style={{
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease-in-out',
                          display: 'inline-block',
                          fontSize: '0.8rem',
                          color: '#666'
                        }}
                      >
                        ▶
                      </span>
                      <span
                        style={{
                          fontWeight: '500',
                          color: '#333',
                          fontSize: '0.95rem'
                        }}
                      >
                        {name}
                      </span>
                    </div>
                    <span
                      style={{
                        fontWeight: 'bold',
                        color: '#4CAF50',
                        fontSize: '1.1rem',
                        marginLeft: '12px'
                      }}
                    >
                      {count} {count === 1 ? 'unit' : 'units'}
                    </span>
                  </div>

                  {/* Expandable content */}
                  {isExpanded && (
                    <div
                      style={{
                        padding: '0 8px 8px 8px',
                        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                        animation: 'fadeIn 0.2s ease-in-out'
                      }}
                    >
                      {orderDetails.map((detail, index) => {
                        // Calculate background color based on this specific order's age
                        const orderDaysSince = getDaysSinceOrder(detail.orderDate);
                        const orderBackgroundColor = getOrderRowBackgroundColor(orderDaysSince, detail.status);

                        return (
                          <div
                            key={`${detail.orderId}-${index}`}
                            onClick={() => onNavigateToOrder(detail.orderId)}
                            style={{
                              padding: '8px 10px',
                              margin: '4px 0',
                              fontSize: '0.9rem',
                              color: '#555',
                              backgroundColor: orderBackgroundColor,
                              borderRadius: '3px',
                              border: '1px solid rgba(0, 0, 0, 0.08)',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out'
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.transform = 'translateX(4px)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.transform = 'translateX(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            {detail.clientId ? (
                              <Link
                                to={`/clients?open=${detail.clientId}`}
                                onClick={e => e.stopPropagation()}
                                style={{
                                  color: '#2196F3',
                                  textDecoration: 'none',
                                  fontWeight: '500',
                                  transition: 'color 0.2s'
                                }}
                                onMouseEnter={e => (e.currentTarget.style.color = '#1976D2')}
                                onMouseLeave={e => (e.currentTarget.style.color = '#2196F3')}
                              >
                                {detail.customerName}
                              </Link>
                            ) : (
                              <span>{detail.customerName}</span>
                            )}
                            {renderCustomerTypeBadge(detail.customerType)} • {detail.orderDate} • {detail.quantity}{' '}
                            {detail.quantity === 1 ? 'unit' : 'units'}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
